import { Resource } from './resource';
import { CaptureGroup, MultiPointInstallation, Organization } from './api';
import { Filter } from '../types/filters';
import { Sort, SortDirection, SortColumn } from '../types/sort';

export type HighlightableCaptureGroup = CaptureGroup & {
  highlighted: boolean;
};

export type MultiPointInstallationCapturesState = {
  page: number;

  filters: Array<Filter>;
  sort: Sort;

  resource: Resource<{
    mpi: MultiPointInstallation;
    captureGroups: {
      total: number;
      total_accuracy: number;
      groups: Array<HighlightableCaptureGroup>;
      min_video_starts_at: string;
    };
    selectedCaptureGroupId: CaptureGroup['id'] | null;
    seek: number;

    fetchingMoreMPIs: Resource<null>; // There is no data in this resource, it's only used as a status
  }>;
};

export enum MultiPointInstallationCapturesActionTypes {
  MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_PAGE = 'MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_PAGE',
  MULTI_POINT_INSTALLATION_CAPTURES_LEAVE_PAGE = 'MULTI_POINT_INSTALLATION_CAPTURES_LEAVE_PAGE',

  MULTI_POINT_INSTALLATION_CAPTURES_SELECT_CAPTURE_GROUP = 'MULTI_POINT_INSTALLATION_CAPTURES_SELECT_CAPTURE_GROUP',
  MULTI_POINT_INSTALLATION_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK = 'MULTI_POINT_INSTALLATION_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK',
  MULTI_POINT_INSTALLATION_CAPTURES_PUSH_NEW = 'MULTI_POINT_INSTALLATION_CAPTURES_PUSH_NEW',
  MULTI_POINT_INSTALLATION_CAPTURES_CLEAR_HIGHLIGHT = 'MULTI_POINT_INSTALLATION_CAPTURES_CLEAR_HIGHLIGHT',

  MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_FILTERS = 'MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_FILTERS',
  MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_SORT = 'MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_SORT',

  MULTI_POINT_INSTALLATION_CAPTURES_LOADING = 'MULTI_POINT_INSTALLATION_CAPTURES_LOADING',
  MULTI_POINT_INSTALLATION_CAPTURES_ERROR = 'MULTI_POINT_INSTALLATION_CAPTURES_ERROR',
  MULTI_POINT_INSTALLATION_CAPTURES_LOAD_COMPLETE = 'MULTI_POINT_INSTALLATION_CAPTURES_LOAD_COMPLETE',

  MULTI_POINT_INSTALLATION_CAPTURES_SET_PAGE = 'MULTI_POINT_INSTALLATION_CAPTURES_SET_PAGE',
  MULTI_POINT_INSTALLATION_CAPTURES_NEXT_PAGE_LOAD_COMPLETE = 'MULTI_POINT_INSTALLATION_CAPTURES_NEXT_PAGE_LOAD_COMPLETE',
  MULTI_POINT_INSTALLATION_CAPTURES_NEXT_PAGE_LOAD_ERROR = 'MULTI_POINT_INSTALLATION_CAPTURES_NEXT_PAGE_LOAD_ERROR',

  MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS = 'MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS',
  MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE = 'MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE',
}

export type MultiPointInstallationCapturesAction =
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_PAGE;
      id: MultiPointInstallation['id'];
      params: {
        capture_id?: number;
        t?: number;
      };
      filters: Array<Filter>;
      sort: Sort;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_LEAVE_PAGE;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_FILTERS;
      filters: Array<Filter>;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CHANGE_SORT;
      direction: SortDirection;
      column: SortColumn;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_LOADING;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_ERROR;
      error: any;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_SELECT_CAPTURE_GROUP;
      id: CaptureGroup['id'];
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_LOAD_COMPLETE;
      mpi: MultiPointInstallation;
      captureGroups: {
        capture_groups_count: number;
        total_accuracy: number;
        groups: Array<CaptureGroup>;
        min_video_starts_at: string;
      };
      selectedCaptureGroupId: CaptureGroup['id'] | null;
      seek: number;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK;
      seek: number;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_PUSH_NEW;
      totalAccuracy: number;
      captureGroups: Array<CaptureGroup>;
      totalCaptureGroups: number;
      minVideoStartsAt: string;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_SET_PAGE;
      value: number;
      mpiId: MultiPointInstallation['id'];
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_NEXT_PAGE_LOAD_ERROR;
      error: any;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_NEXT_PAGE_LOAD_COMPLETE;
      captureGroups: {
        total: number;
        total_accuracy: number;
        groups: Array<CaptureGroup>;
      };
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_CLEAR_HIGHLIGHT;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS;
      mpiId: MultiPointInstallation['id'];
      ids: Array<CaptureGroup['id']>;
    }
  | {
      type: MultiPointInstallationCapturesActionTypes.MULTI_POINT_INSTALLATION_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE;
      ids: Array<CaptureGroup['id']>;
    };
